import * as React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Select from '@mui/material/Select';
import { TableContainer, Paper, Table, TableHead, TableRow, 
        TableBody, IconButton, Box, FormControl, Typography,
        MenuItem, Backdrop, CircularProgress, Stack, ListItemText, 
        OutlinedInput} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import SubBandView from './SubBandView';
import ConfirmationDialog from '../util/ConfirmationDialog';
import * as Utils from '../util/Utils'
import { ProjectContext } from './ProjectContext';
import { styled } from '@mui/material/styles';
import 'dayjs/locale/en-gb';
import dayjs, { Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    borderBottom: "none",
    padding: '10px 10px 0 10px'
  },
}));

export default function SubBandBox(props: {
  corrConfig: any,
  corrSetting: any,
  utcDate: Dayjs,
  setSubBandConfigs: (subBandConfigs: any[], sort: boolean) => void,
  bandSetting: any
}) {
  const projectContext = React.useContext(ProjectContext);

  const [openReorderBackdrop, setOpenReorderBackdrop] = React.useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = React.useState(false);

  const [selectedZoomBandIndex, setSelectedZoomBandIndex] = React.useState(-1);
  const [subBandConfigs, setSubBandConfigs] = 
    React.useState<any[]>(props.corrConfig['sub_band_configuration'] || []);

  React.useEffect(() => {
    setSubBandConfigs(props.corrConfig['sub_band_configuration'] || []);
  }, [props.corrConfig]);

  const hasSubBands = (): boolean => {
    return projectContext.hasSubBands(
      props.corrConfig
    );
  }

  const deleteSubBandConfig = (selectedBand: number) => {
    setSelectedZoomBandIndex(selectedBand);
    setOpenConfirmationDialog(true);
  }

  const handleConfirmationNo = () => {
    setSelectedZoomBandIndex(-1);
    setOpenConfirmationDialog(false);
  }

  const handleConfirmationYes = () => {
    const newConfigs = [...subBandConfigs];
    newConfigs.splice(selectedZoomBandIndex, 1);
    props.setSubBandConfigs(newConfigs, true);

    setSelectedZoomBandIndex(-1);
    setOpenConfirmationDialog(false);
  }

  const getZoomFreqConfig = (id: number) => {
    const configs = subBandConfigs.filter((config) => {
      return(config['id'] === id);
    });
    if (configs.length > 0) {
      const config = configs[0];
      return config;
    }
    return {};
  }

  const getZoomBand = (id: number) => {
    const configs = subBandConfigs.filter((config) => {
      return (config['id'] === id);
    });
    if (configs.length > 0) {
      const config = configs[0];
      const zooms = getZooms();

      const bands = zooms.filter((band: any) => {
        return (band['name'] === config['zoom']);
      });

      if (bands.length > 0) {
        return bands[0];
      }
    }
    return {};
  }

  const setCentreFreq = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
    id: number) => {
    const bands = subBandConfigs.filter((zoom) => {
      return (zoom['id'] === id);
    });
    if (bands.length > 0) {
      const zoomBand = bands[0];
      zoomBand['centre_frequency'] = event.target.value;
      const newZoomBand = Utils.caluculateBand(
          zoomBand['centre_frequency'], 
          props.corrConfig['frequency_configuration'],
          props.corrSetting
        );
      
      if (newZoomBand && !newZoomBand['messages']) {
        if (newZoomBand['band'] !== zoomBand['band'] 
          || newZoomBand['subband'] !== zoomBand['subband']) {
        
          zoomBand['band'] = newZoomBand['band'];
          zoomBand['subband'] = newZoomBand['subband'];
          // if there is a change in band or subband, sort with delay
          setTimeout(() => {
            setOpenReorderBackdrop(true);
            props.setSubBandConfigs([...subBandConfigs], true);
            setTimeout(() => {
              setOpenReorderBackdrop(false);
            }, 1000);
          }, 3000)
        }
      }

      props.setSubBandConfigs([...subBandConfigs], false);
    }
  }

  const setFieldValue = (
    value: string,
    id: number, fieldName: string) => {
    const configs = subBandConfigs.filter((config) => {
      return (config['id'] === id);
    });
    if (configs.length > 0) {
      const config = configs[0];
      config[fieldName] = 
      value;
      props.setSubBandConfigs([...subBandConfigs], false);
    }
  }

  const getZooms = () => {
    return Utils.getZoomConfigurations(
      projectContext.correlatorSettings,
      props.corrConfig['correlator_setting']);
  }

  const getStatusRow = (status: any) => (
    <TableRow>
      <TableCell align="center" colSpan={10} 
        sx={{ height: '25px', padding: 0, color: status['color'] }}>
        {status['status'] === 'valid' ? '' : status['status'] + ': ' + status['message']}
      </TableCell>
    </TableRow>
  )

  const renderZoomValue = (selected: any) => {
    if (!selected) {
      return (
        <Typography variant="body1"></Typography>
      );
    }
    const zoom = getZoomBand(selected['id']);
    if (!zoom) {
      return (
        <Typography variant="body1"></Typography>
      );
    } else {
      return (
        <Stack direction="row" spacing={2} alignItems={'center'}>
          <Typography variant="body1">
            {zoom['name'] || ''}
          </Typography>
          <Typography variant="body2">
            {
              (zoom['points'] && zoom['spectral_resolution']) 
              ? '' + (zoom['points'] * (zoom['spectral_resolution'])).toFixed(3) + 'kHz'
              : ''
            }
          </Typography>
          <Typography variant="body2">
            {
              zoom['data_rate'] 
              ? '' + zoom['data_rate'] + 'kB/s'
              : ''
            } 
          </Typography>
        </Stack>
      );
    }
  }

  const subBandTable = (
    <TableContainer component={Paper} sx={{ 
      width: 'calc(100% - 10px)', height: 'calc(100% - 30px)' }}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell align="center">Band</TableCell>
            <TableCell align="center">Sub Band</TableCell>
            <TableCell width={'30%'} align="center">Zoom Config</TableCell>
            <TableCell width={'15%'} align="center">Center Freq (MHz)</TableCell>
            <TableCell width={'45%'} align="center">Comment</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subBandConfigs.map((row: any, index: number) => (
            <React.Fragment key={'zoom_' + index}>
              <TableRow
                key={'row-' + row['band'] + '-' + row['subband'] + '-' + index}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <StyledTableCell align="center" sx={{color: row.band===0 ? '#c62828':''}}>
                  {row.band}
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ color: row.subband === 0 ? '#c62828' : '' }}>
                  {row.subband}
                </StyledTableCell>

                <StyledTableCell align="center">
                  <FormControl fullWidth>
                    <Select
                      labelId="zoom-selection-label"
                      id="zoom-selection-value"
                      displayEmpty={true}
                      renderValue={renderZoomValue}
                      error={!Boolean(getZoomFreqConfig(row['id'])['zoom'])}
                      value={row}
                      onChange={(e: any) => setFieldValue(e.target.value, row['id'], 'zoom')}
                    >
                      {getZooms().map((config: any, index: number) => (
                        <MenuItem key={config['name'] + '_' + index} value={config['name']} 
                          disabled={
                            Utils.zoomExists(
                              props.corrConfig['sub_band_configuration'],
                              row['band'], 
                              row['subband'], 
                              config['name'])}
                        >
                          <ListItemText primary={config['name']}
                            secondary={
                              <Stack direction="row" spacing={2}>
                                <Typography variant="body2">
                                  {config['points']} points
                                </Typography>
                                <Typography variant="body2">
                                  {config['spectral_resolution'].toFixed(3)} {config['unit']}
                                </Typography>
                                <Typography variant="body2">
                                  {(config['points'] * config['spectral_resolution']).toFixed(3)} kHz
                                </Typography>
                                <Typography variant="body2">
                                  {config['data_rate']} kB/s
                                </Typography>
                              </Stack>
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>  
                </StyledTableCell>

                <StyledTableCell align="center">
                  <OutlinedInput id={`center-freq-{index}`}
                    value={getZoomFreqConfig(row['id'])['centre_frequency'] || ''}
                    fullWidth
                    error={!Boolean(getZoomFreqConfig(row['id'])['centre_frequency'])}
                    onChange={(e) => setCentreFreq(e, row['id'])}
                  />
                </StyledTableCell>

                <StyledTableCell align="center">
                  <OutlinedInput id={`zoom-comment-{index}`}
                    value={getZoomFreqConfig(row['id'])['comment'] || ''}
                    fullWidth
                    onChange={(e) => setFieldValue(e.target.value, row['id'], 'comment')}
                  />
                </StyledTableCell>

                <StyledTableCell align="center" padding='none'>
                  <IconButton color='primary'
                    aria-label="delete zoom band"
                    onClick={(e) => deleteSubBandConfig(index)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </StyledTableCell>

              </TableRow>
              {getStatusRow(Utils.validateZoomBand(
                projectContext.correlatorSettings,
                props.corrConfig, row))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <React.Fragment>
      <ConfirmationDialog open={openConfirmationDialog}
        title={'Confirm delete zoom configuration'}
        message={'Do you want to delete zoom configuration?'}
        handleNo={handleConfirmationNo}
        handleYes={handleConfirmationYes}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openReorderBackdrop}
      >
        <Stack>
          <Typography>Reordering zoom bands</Typography>
          <CircularProgress color="inherit" />
        </Stack>
      </Backdrop>

      <SubBandView
        corrSetting={props.corrSetting}
        subBandConfigs={subBandConfigs}
        setSubBandConfigs={props.setSubBandConfigs}
        freqConfig={(props.corrConfig||{})['frequency_configuration']||{}}
        bandSetting={props.bandSetting}
        hasSubBands={hasSubBands()}
      />

      {
          hasSubBands()
          &&
          <Box sx={{ marginTop: '10px', padding: '5px', 
            width: '100%', height: 'calc(100vh - 450px)' }}>
            {subBandTable}
          </Box>
      }
    </React.Fragment>
  );
};
